/**
 * Componente seletor de serviços com cards visuais.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { PublicService } from "../types";
import { resolveImageUrl } from "../utils";
import { formatBRL } from "../../../lib/currency";
import { formatDurationMinutes } from "../../../lib/time";
import { getPriceQuote } from "../../../services/bookingPaymentClient";
import {
  FiCheck,
  FiClock,
  FiImage,
  FiMaximize2,
  FiPackage,
  FiScissors,
} from "react-icons/fi";
import { Card } from "../../../components/ui";
import { ServiceDetailsModal } from "./ServiceDetailsModal";

interface ServiceSelectorProps {
  establishmentId?: string | null;
  services: PublicService[];
  multiEnabled: boolean;
  selectedServiceIds: string[];
  selectedServiceId: string;
  selectedProviderId?: string;
  selectedProviderName?: string;
  onClearProviderFilter?: () => void;
  isSelected: (id: string) => boolean;
  toggleService: (id: string) => void;
  onPriceMapChange?: (priceMap: Record<string, number | null>) => void;
  onlinePaymentsEnabled?: boolean | null;
  embedded?: boolean;
}

export function ServiceSelector({
  establishmentId,
  services,
  multiEnabled,
  selectedServiceIds,
  selectedProviderId,
  selectedProviderName,
  onClearProviderFilter,
  isSelected,
  toggleService,
  onPriceMapChange,
  onlinePaymentsEnabled,
  embedded = false,
}: ServiceSelectorProps) {
  const [priceMap, setPriceMap] = useState<Record<string, number | null>>({});
  const [quoteRetry, setQuoteRetry] = useState(0);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [failedImageIds, setFailedImageIds] = useState<Record<string, boolean>>({});
  const [detailsServiceId, setDetailsServiceId] = useState<string | null>(null);

  useEffect(() => {
    if (!establishmentId || services.length === 0) {
      setLoadingPrices(false);
      setPriceMap({});
      onPriceMapChange?.({});
      return;
    }

    let mounted = true;
    const priced = services.filter(
      (service) =>
        service.showPrice && typeof service.price === "number" && service.price > 0
    );

    if (priced.length === 0) {
      setLoadingPrices(false);
      setPriceMap({});
      onPriceMapChange?.({});
      return;
    }

    if (onlinePaymentsEnabled === false) {
      const catalogPrices = Object.fromEntries(
        priced.map((service) => [service.id, Number(service.price)])
      );
      setPriceMap(catalogPrices);
      onPriceMapChange?.(catalogPrices);
      setLoadingPrices(false);
      return;
    }

    setLoadingPrices(true);
    setPriceMap({});
    onPriceMapChange?.({});
    Promise.all(
      priced.map(async (service) => {
        // Uma falha não apaga as demais cotações nem transforma preço de
        // catálogo em promessa de valor final para pagamento.
        try {
          const quote = await getPriceQuote({
            establishmentId,
            paymentType: "credit",
            creditPayoutTerm: "D14",
            serviceId: service.id,
          });
          const finalAmount = Number(quote.finalAmount);
          return [
            service.id,
            Number.isFinite(finalAmount) && finalAmount > 0 ? finalAmount : null,
          ] as const;
        } catch {
          return [service.id, null] as const;
        }
      })
    )
      .then((entries) => {
        if (!mounted) return;
        const next: Record<string, number | null> = {};
        entries.forEach(([id, value]) => {
          next[id] = value;
        });
        setPriceMap(next);
        onPriceMapChange?.(next);
      })
      .finally(() => {
        if (mounted) setLoadingPrices(false);
      });

    return () => {
      mounted = false;
    };
  }, [establishmentId, onPriceMapChange, onlinePaymentsEnabled, quoteRetry, services]);

  const getDisplayedPrice = useCallback(
    (service: PublicService) => {
      if (!service.showPrice || typeof service.price !== "number") return null;
      if (loadingPrices) return null;
      if (service.price <= 0) return service.price;
      return priceMap[service.id] ?? null;
    },
    [loadingPrices, priceMap]
  );

  const getPriceLabel = useCallback(
    (service: PublicService) => {
      if (!service.showPrice || typeof service.price !== "number") {
        return "Valor no atendimento";
      }
      const displayedPrice = getDisplayedPrice(service);
      if (displayedPrice == null) return "Valor indisponível";
      return service.priceStartingFrom
        ? `A partir de ${formatBRL(displayedPrice)}`
        : formatBRL(displayedPrice);
    },
    [getDisplayedPrice]
  );

  const detailsService = useMemo(
    () => services.find((service) => service.id === detailsServiceId) || null,
    [detailsServiceId, services]
  );

  const selectedServices = services.filter((service) =>
    selectedServiceIds.includes(service.id)
  );

  if (services.length === 0) {
    const hasProviderFilter = Boolean(selectedProviderId);
    const providerLabel = selectedProviderName || "este profissional";
    return (
      <Card variant="bordered" className="mb-6 border-amber-200 bg-amber-50/70">
        <div className="flex items-start gap-3 text-amber-800">
          <div className="rounded-2xl bg-amber-100 p-3 text-xl">
            <FiPackage aria-hidden="true" />
          </div>
          <div>
            <p className="font-semibold">
              {hasProviderFilter
                ? "Nenhum serviço disponível para o profissional selecionado"
                : "Nenhum serviço disponível"}
            </p>
            <p className="mt-1 text-sm text-amber-700">
              {hasProviderFilter
                ? `${providerLabel} não atende serviços disponíveis nesta unidade/data.`
                : "Usando duração padrão do estabelecimento."}
            </p>
            {hasProviderFilter && onClearProviderFilter && (
              <button
                type="button"
                onClick={onClearProviderFilter}
                className="mt-3 inline-flex h-9 items-center rounded-xl border border-amber-300 bg-white px-3 text-xs font-bold text-amber-800 transition hover:bg-amber-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100"
              >
                Ver todos os serviços
              </button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <section
      className={
        embedded
          ? "animate-fade-in overflow-hidden"
          : "animate-fade-in overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/70 sm:p-6"
      }
    >
      {!embedded && (
        <div className="mb-4 flex items-center gap-2">
          <FiScissors className="h-5 w-5 text-slate-600" aria-hidden="true" />
          <h2 className="text-2xl font-bold leading-tight text-[#233055]">
            Escolha o serviço
          </h2>
        </div>
      )}

      {selectedProviderId && selectedProviderName && (
        <div className="mb-4 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900">
          <p className="font-semibold">
            Filtrando por profissional: {selectedProviderName}
          </p>
          {onClearProviderFilter && (
            <button
              type="button"
              onClick={onClearProviderFilter}
              className="mt-2 inline-flex h-9 items-center rounded-xl border border-primary-200 bg-white px-3 text-xs font-semibold text-primary-700 transition hover:bg-primary-100"
            >
              Ver todos os serviços
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3">
        {services.map((service) => {
          const selected = isSelected(service.id);
          const priceLabel = getPriceLabel(service);
          const imageSrc = service.imageUrl ? resolveImageUrl(service.imageUrl) : "";
          const showImage = Boolean(imageSrc) && !failedImageIds[service.id];

          return (
            <div
              key={service.id}
              className={`group flex items-center gap-3 overflow-hidden rounded-2xl border bg-white p-2 transition sm:h-full sm:flex-col sm:items-stretch sm:gap-0 sm:p-0 ${
                selected
                  ? "border-primary-500 shadow-[0_0_0_1px_rgba(124,58,237,0.35)]"
                  : "border-slate-200 hover:border-primary-300"
              }`}
            >
              {/* A imagem abre os detalhes e o resto da linha seleciona: uma
                  funcao por alvo, sem botao flutuante roubando largura do nome. */}
              <button
                type="button"
                onClick={() => setDetailsServiceId(service.id)}
                aria-label={`Ver detalhes de ${service.name}`}
                data-testid={`service-media-${service.id}`}
                className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100 transition focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 sm:aspect-[16/10] sm:h-auto sm:w-full sm:rounded-none"
              >
                {showImage ? (
                  <>
                    {/* Fundo desfocado preenche a moldura sem recortar a foto original. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full scale-125 object-cover opacity-45 blur-2xl transition-transform duration-300 group-hover:scale-[1.35]"
                    />
                    {/* A foto em si nao recebe zoom no hover: escalar um object-contain
                        recortaria justamente as bordas que queremos manter visiveis. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt={service.name}
                      className="relative z-10 h-full w-full object-contain"
                      onError={() =>
                        setFailedImageIds((previous) => ({
                          ...previous,
                          [service.id]: true,
                        }))
                      }
                    />
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
                    <FiImage className="h-6 w-6 sm:h-8 sm:w-8" aria-hidden="true" />
                  </div>
                )}

                {selected && (
                  <span className="absolute left-1 top-1 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-white shadow-sm sm:left-2 sm:top-2 sm:h-7 sm:w-7">
                    <FiCheck aria-hidden="true" />
                  </span>
                )}

                {/* Marca de ampliar: diz que a foto abre, sem virar um segundo
                    alvo de toque disputando espaco com o nome. */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-1 right-1 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-[11px] text-slate-700 shadow-sm ring-1 ring-slate-900/5 backdrop-blur transition group-hover:bg-white group-hover:text-primary-700 sm:bottom-2 sm:right-2 sm:h-7 sm:w-7 sm:text-xs"
                >
                  <FiMaximize2 />
                </span>
              </button>

              <button
                type="button"
                data-testid={`service-select-${service.id}`}
                onClick={() => toggleService(service.id)}
                className="flex min-h-[44px] min-w-0 flex-1 flex-col justify-center rounded-xl px-1 text-left transition focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 sm:rounded-none sm:p-3.5"
                aria-pressed={selected}
              >
                <h3 className="line-clamp-2 text-base font-semibold leading-tight text-[#233055] sm:text-xl">
                  {service.name}
                </h3>

                <div className="mt-1.5 flex items-center justify-between gap-3 text-sm text-slate-600 sm:mt-2">
                  <span className="inline-flex items-center gap-1.5">
                    <FiClock className="h-4 w-4" aria-hidden="true" />
                    {formatDurationMinutes(service.durationMinutes)}
                  </span>
                  <span className="font-semibold text-primary-700">
                    {loadingPrices && service.showPrice ? "..." : priceLabel}
                  </span>
                </div>

                {service.description ? (
                  <p className="mt-2 hidden line-clamp-2 text-sm leading-snug text-slate-600 sm:block">
                    {service.description}
                  </p>
                ) : null}
              </button>
            </div>
          );
        })}
      </div>

      {Object.values(priceMap).some((price) => price === null) && (
        <button
          type="button"
          onClick={() => setQuoteRetry((attempt) => attempt + 1)}
          className="mt-3 min-h-[44px] rounded-xl border border-amber-300 px-3 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100"
        >
          Tentar calcular o valor novamente
        </button>
      )}

      {multiEnabled && selectedServiceIds.length > 0 && (
        <div className="mt-4 rounded-2xl border border-primary-100 bg-primary-50/50 p-4 text-sm text-slate-700">
          <div className="font-medium">
            Serviços selecionados ({selectedServiceIds.length}):
          </div>
          <ul className="mt-2 space-y-1 text-slate-600">
            {selectedServices.map((service) => (
              <li
                key={service.id}
                className="flex items-center justify-between gap-3 leading-tight"
              >
                <span className="min-w-0 truncate">{service.name}</span>
                <span className="flex-shrink-0 text-xs font-semibold text-primary-700">
                  {formatDurationMinutes(service.durationMinutes)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-3">
            <span className="font-medium">Duração total estimada:</span>{" "}
            {formatDurationMinutes(
              selectedServices.reduce((acc, service) => acc + service.durationMinutes, 0)
            )}
          </div>

          {selectedServices.some(
            (service) => service.showPrice && typeof service.price === "number"
          ) ? (
            <div className="mt-1">
              <span className="font-medium">Preço total:</span> Valor calculado antes do
              pagamento
            </div>
          ) : null}
        </div>
      )}

      <ServiceDetailsModal
        service={detailsService}
        priceLabel={
          detailsService
            ? loadingPrices && detailsService.showPrice
              ? "Calculando valor..."
              : getPriceLabel(detailsService)
            : ""
        }
        selected={detailsService ? isSelected(detailsService.id) : false}
        onToggleSelect={() => {
          if (!detailsService) return;
          toggleService(detailsService.id);
          setDetailsServiceId(null);
        }}
        onClose={() => setDetailsServiceId(null)}
      />
    </section>
  );
}
